'use client';

import { useEffect, useState } from 'react';
import type { ReferencePrompt } from '@/app/types';
import {
  getProbedReferenceVideoDuration,
  getReferenceVideoDuration,
  getReferenceVideoUrl,
  hasReferenceVideoDurationProbeFailed,
  isReferenceVideoPrompt,
  markReferenceVideoDurationProbeFailed,
  setProbedReferenceVideoDuration,
} from '@/app/utils';
import { getVideoMetadata } from '@/app/utils/client';

/**
 * 探测单个视频地址的时长并写入共享缓存，已知则直接复用。
 *
 * 供「选中主体时校验时长组合」用：那一步必须在把主体加进列表之前就拿到时长，
 * 不能等 `useReferenceVideoDurations` 的 effect 慢慢补。
 */
export const resolveReferenceVideoDuration = async (videoUrl: string | undefined): Promise<number | undefined> => {
  if (!videoUrl) return undefined;

  const cached = getProbedReferenceVideoDuration(videoUrl);
  if (cached !== undefined) return cached;
  if (hasReferenceVideoDurationProbeFailed(videoUrl)) return undefined;

  try {
    const { duration } = await getVideoMetadata(videoUrl, { includeDuration: true });
    if (duration && duration > 0) {
      setProbedReferenceVideoDuration(videoUrl, duration);
      return duration;
    }
  } catch {
    // 探测失败静默处理，由调用方按「时长未知」保守处理。
  }

  markReferenceVideoDurationProbeFailed(videoUrl);
  return undefined;
};

/**
 * 探测视频类参考的真实时长，供 Q2Pro「1 个 5-8s 或 2 个 5s 内」额度判断使用。
 *
 * 视频主体（element）的接口不返回时长，上传的视频在写回 prompt.duration 前也拿不到，
 * 两者都只能靠加载 URL 元数据探测。结果写进 `reference-model-capabilities` 的共享缓存，
 * 额度规则本身仍然只有那一处，这里只负责把时长喂过去。
 *
 * @returns 探测轮次，仅用于在探测完成后触发重算（值本身无意义）
 */
export const useReferenceVideoDurations = (referencePrompts: ReferencePrompt[]): number => {
  const [probeRevision, setProbeRevision] = useState(0);

  // 只依赖「待探测的地址」列表：已经知道时长的（prompt.duration 或缓存里有）、以及探测失败过的
  // 直接排除，既避免 referencePrompts 每次重建触发重复探测，也不跟 create 页已有的元数据逻辑抢活。
  const pendingUrlsKey = referencePrompts
    .filter((reference) => isReferenceVideoPrompt(reference) && getReferenceVideoDuration(reference) === undefined)
    .map((reference) => getReferenceVideoUrl(reference))
    .filter((url): url is string => !!url && !hasReferenceVideoDurationProbeFailed(url))
    .join('|');

  useEffect(() => {
    const pendingUrls = pendingUrlsKey.split('|').filter(Boolean);

    if (pendingUrls.length === 0) {
      return;
    }

    let cancelled = false;

    void Promise.all(pendingUrls.map((url) => resolveReferenceVideoDuration(url))).then(() => {
      // 无论探到还是探失败，状态都变了（失败也会被标记），需要重算一次额度。
      if (!cancelled) {
        setProbeRevision((revision) => revision + 1);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [pendingUrlsKey]);

  return probeRevision;
};
